// A greyed suggestion of the sidebar, shown while the report loads so the column is not simply
// empty beside the loading field. It is plain DOM rather than a component because the real sidebar
// is a React tree that only mounts once the survey has arrived -- until then there is nothing in
// the column to render into.
//
// It lives with the preloader because the loading experience is the preloader's, and because the
// first caller is the preloader itself, before the page model exists. It was written twice: once
// here and once in the stats lib, and the two disagreed about where the switches come from, what
// the label is wrapped in, and when the placeholder may come back. Both callers now reach the one
// below.

import { buildSwitchContext, visibleSwitches } from '@aytm/app/stats/page/sidebar/components/lib/switches'

const SHELL_CLASS = 'stats-sidebar-shell'

// Widths of the placeholder lines standing in for a panel's contents, as percentages. Uneven on
// purpose: an even stack reads as a table rather than as text yet to arrive.
const LINES = [72, 88, 60, 80, 52, 84, 66]

// Marks the column as handed over. Kept on the column rather than in a module variable so it
// shares that column's own lifetime: the report re-enters the loading state on every reload, and
// by then the real sidebar owns the column and the placeholder must not come back over it.
const HANDED_OVER = 'statsSidebarShellHandedOver'

// The strip's own gate, read off whichever account state has arrived. Once the page model exists
// its registry is the answer, qualified by the report it resolved. Before that -- which is when the
// preloader runs -- only the served payload has arrived, so the two terms the gate reads are
// derived from it here: hidden is the stronger and disabled the weaker, so anything not on is
// disabled while only what is off is also hidden. Reading the stronger one alone left the Mapping
// switch standing on a merely locked subscription, which the strip withholds.
const railSwitches = () => {
  const model = window.StatPage?.model

  if (model?.features) {
    return visibleSwitches(
      buildSwitchContext({
        features: model.features,
        isReportVirtual: model.isReportVirtual?.() || false,
        isReportXpert: model.isReportXpert?.() || false
      })
    )
  }

  const arrived = window.features
  if (!arrived) return []

  // No report is resolved yet and no mode is set, which is what makes each a single term here: the
  // report-type and mode qualifications the registry carries have nothing yet to qualify.
  const features = Object.keys(arrived).reduce((acc, key) => {
    const status = arrived[key]?.status
    acc[key] = { ...arrived[key], hidden: 'off' === status, disabled: 'on' !== status }
    return acc
  }, {})

  return visibleSwitches(buildSwitchContext({ features }))
}

export const mountSidebarShell = () => {
  const aside = document.querySelector('.-sidebar')
  if (!aside || aside.dataset[HANDED_OVER] || aside.querySelector(`.${SHELL_CLASS}`)) return

  const switches = railSwitches()
  // nothing known about the strip yet -- an invented rail would only be replaced by a different one
  if (!switches.length) return

  const rail = switches
    .map(
      ({ icon, label }) =>
        `<span class="${SHELL_CLASS}__item"><i class="icon-aytmfd-${icon}"></i>` +
        // the strip states the label's type on the label; stated on the item it would size the
        // anonymous box a bare text node sits in, which is not the same measurement
        `<span class="${SHELL_CLASS}__item-text">${label}</span></span>`
    )
    .join('')
  const lines = LINES.map(width => `<span class="${SHELL_CLASS}__line" style="width:${width}%"></span>`).join('')

  const shell = document.createElement('div')
  shell.className = SHELL_CLASS
  // it stands in for the sidebar rather than describing it, so it is not announced
  shell.setAttribute('aria-hidden', 'true')
  shell.innerHTML = `<div class="${SHELL_CLASS}__rail">${rail}</div><div class="${SHELL_CLASS}__panel">${lines}</div>`

  // prepended to the aside itself: the wrapper the page yields before React mounts is inset by the
  // layout stylesheet, and a shell placed inside it floats away from the column edge
  aside.prepend(shell)
}

export const unmountSidebarShell = () => {
  const shell = document.querySelector(`.${SHELL_CLASS}`)
  if (shell) shell.remove()

  const aside = document.querySelector('.-sidebar')
  if (aside) aside.dataset[HANDED_OVER] = '1'
}
